import cx from 'classnames';
import * as React from 'react';
import Time from 'components/Time';

type DefaultProps = {
  format: 'long' | 'short' | 'time';
};

export type Props = {
  className?: string;
  date: Date;
  lowercase?: boolean;
} & DefaultProps;

export default class Timestamp extends React.Component<Props> {
  static defaultProps: DefaultProps = {
    format: 'long',
  };

  shouldComponentUpdate(nextProps: Props) {
    const same =
      this.props.date.getTime() === nextProps.date.getTime() &&
      this.props.format === nextProps.format;
    return !same;
  }

  getDayInterval = (date: Date) => {
    return Math.floor(
      (date.getTime() - date.getTimezoneOffset() * 60 * 1000) / (1000 * 60 * 60 * 24)
    );
  };

  getDayIntervalDifference = (a: Date, b: Date) => {
    return this.getDayInterval(a) - this.getDayInterval(b);
  };

  renderTimestamp = () => {
    const { date, format, lowercase } = this.props;

    const today = lowercase ? 'today' : 'Today';
    const tomorrow = lowercase ? 'tomorrow' : 'Tomorrow';
    const yesterday = lowercase ? 'yesterday' : 'Yesterday';

    if (format !== 'time') {
      const diff = this.getDayIntervalDifference(date, new Date(Date.now()));
      const absDiff = Math.abs(diff);

      if (format === 'long') {
        if (absDiff >= 7) {
          // Show full date (e.g., March 26, 2015)
          return <Time date={date} month="long" day="numeric" year="numeric" />;
        } else if (absDiff >= 2) {
          // Show full weekday (e.g., Sunday)
          return <Time date={date} weekday="long" />;
        } else if (absDiff === 1) {
          return <span>{diff > 0 ? tomorrow : yesterday}</span>;
        } else {
          return <span>{today}</span>;
        }
      } else {
        if (absDiff >= 365) {
          // Show full numeric date (e.g., 3/28/15)
          return <Time date={date} month="numeric" day="numeric" year="2-digit" />;
        } else if (absDiff >= 7) {
          // Show abbreviated date (e.g., Jun 12)
          return <Time date={date} month="short" day="numeric" />;
        } else if (absDiff >= 1) {
          // Show abbreviated weekday (e.g., Sun)
          return <Time date={date} weekday="short" />;
        }
      }
    }

    // Just the time (e.g., 11:32 AM)
    return <Time date={date} hour="numeric" minute="2-digit" />;
  };

  render() {
    const { className, date } = this.props;

    return React.cloneElement(this.renderTimestamp(), {
      className: cx('Timestamp', className),
      title: date.toString(),
    });
  }
}
